// ---------------------------------------------------------------------------
// Account UI (Proxy Table / wesyarber.net shared accounts) — owned by the
// accounts feature.
//
// Integration contract with the rest of the client:
//   • main.ts imports this module for its side effects and exposes
//     `window.tt = { settings }` (the live Settings instance) before import.
//   • This module mounts its button/menu into the `.account-slot` span that
//     ui.ts places in the top bar (poll/observe until it exists).
//   • Settings sync: read/write via window.tt.settings.data + .save();
//     `window` fires "tt-settings-changed" (detail = SettingsData) on every
//     local save.
//   • Saved decks: the import dialog (import-ui.ts) handles save/load UI for
//     decks; this module owns login/logout/session state and exposes
//     `window.tt.account` = { me, api } for other modules. It also fires a
//     "tt-account-changed" CustomEvent (detail = user | null) on sign-in /
//     sign-out so other modules can refresh.
//
// Two modes, auto-detected:
//   • same-origin (hostname ends with wesyarber.net): calls /sim-api directly
//     with credentials, Google sign-in link works natively.
//   • proxy (localhost / Tailscale dev): calls /account/* on our own server,
//     which forwards to the upstream API (src/server/account.ts) and holds
//     the session in a first-party tt_session cookie. Google OAuth cannot
//     complete cross-origin, so Google sign-in uses device pairing instead:
//     POST /auth/pair/new for a code, open the approve page on wesyarber.net
//     in a new tab (where the user's Google session lives), then poll
//     /auth/pair/claim until the proxy converts the approved session token
//     into our tt_session cookie.
//
// The remote /settings blob is SHARED with Proxy Table — this module only
// ever touches its `tabletop` key and writes the blob back read-modify-write
// (see mergeSettingsBlob, which is pure and unit-tested).
// ---------------------------------------------------------------------------

import type { SettingsData } from "./settings";

export interface AccountUser {
  id: number | string;
  email: string | null;
  name: string;
  googleVerified?: boolean;
}

/**
 * Merge our settings under the `tabletop` key of the shared remote blob
 * without dropping any foreign keys (Proxy Table stores its own there).
 */
export function mergeSettingsBlob(
  remote: unknown,
  tabletop: SettingsData,
): Record<string, unknown> {
  const base =
    remote && typeof remote === "object" && !Array.isArray(remote)
      ? { ...(remote as Record<string, unknown>) }
      : {};
  base.tabletop = tabletop;
  return base;
}

// ------------------------------------------------------------------- api

const sameOrigin =
  typeof location !== "undefined" && location.hostname.endsWith("wesyarber.net");
const API_BASE = sameOrigin ? "/sim-api" : "/account";

/** Fetch wrapper for the account API; `path` is upstream-relative ("/decks"). */
function api(path: string, init?: RequestInit): Promise<Response> {
  return fetch(API_BASE + path, { credentials: "include", ...init });
}

function postJson(path: string, body: unknown): Promise<Response> {
  return api(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

let currentUser: AccountUser | null = null;

// ------------------------------------------------------- device pairing
// Proxy mode only: Google OAuth can't complete on this origin, so we pair
// through wesyarber.net — request a code, have the user approve it in a tab
// on wesyarber.net (where their Google session cookie lives; the approve page
// bounces them through /auth/google first if needed), then poll claim until
// our server proxy has turned the approved token into the tt_session cookie.

const PAIR_UPSTREAM = "https://wesyarber.net/sim-api"; // matches server DEFAULT_UPSTREAM
const PAIR_POLL_MS = 2000;
const PAIR_TIMEOUT_MS = 3 * 60 * 1000;

let pairTimer: ReturnType<typeof setInterval> | null = null;

function stopPairing(): void {
  if (pairTimer) clearInterval(pairTimer);
  pairTimer = null;
}

/** Kick off a pairing attempt, reporting progress into `status`. */
async function startPairing(status: HTMLElement): Promise<void> {
  stopPairing();
  status.textContent = "Starting…";
  let code: string;
  try {
    const res = await postJson("/auth/pair/new", {});
    const body = (await res.json()) as { code?: string };
    if (!res.ok || !body.code) throw new Error();
    code = body.code;
  } catch {
    status.textContent = "Account service unreachable.";
    return;
  }

  const approveUrl = `${PAIR_UPSTREAM}/auth/pair/approve?code=${encodeURIComponent(code)}`;
  window.open(approveUrl, "_blank", "noopener");

  status.textContent = "Waiting for approval in the wesyarber.net tab… ";
  const reopen = document.createElement("a");
  reopen.href = approveUrl;
  reopen.target = "_blank";
  reopen.rel = "noopener";
  reopen.textContent = "reopen page";
  const cancel = document.createElement("a");
  cancel.href = "#";
  cancel.textContent = "cancel";
  cancel.onclick = (e) => {
    e.preventDefault();
    stopPairing();
    status.textContent = "";
  };
  status.append(reopen, " · ", cancel);

  const deadline = Date.now() + PAIR_TIMEOUT_MS;
  pairTimer = setInterval(() => {
    if (Date.now() > deadline) {
      stopPairing();
      status.textContent = "Pairing timed out — try again.";
      return;
    }
    void (async () => {
      let res: Response;
      try {
        res = await api(`/auth/pair/claim?code=${encodeURIComponent(code)}`);
      } catch {
        return; // transient network hiccup — keep polling
      }
      if (res.status === 404) {
        stopPairing();
        status.textContent = "Pairing code expired — try again.";
        return;
      }
      if (!res.ok) return;
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean };
      if (body.ok) {
        // The proxy already set tt_session from the claimed token.
        stopPairing();
        status.textContent = "Signed in.";
        await refreshMe();
        void pullSettings();
      }
    })();
  }, PAIR_POLL_MS);
}

// -------------------------------------------------------- settings sync

const PUSH_DEBOUNCE_MS = 2000;
let pushTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePush(data: SettingsData): void {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void pushSettings(data);
  }, PUSH_DEBOUNCE_MS);
}

async function pushSettings(data: SettingsData): Promise<void> {
  if (!currentUser) return;
  try {
    const res = await api("/settings");
    if (!res.ok) return;
    const blob = ((await res.json()) as { settings?: unknown }).settings ?? null;
    await api("/settings", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(mergeSettingsBlob(blob, data)),
    });
  } catch {
    /* offline / upstream down — local settings are still saved */
  }
}

async function pullSettings(): Promise<void> {
  try {
    const res = await api("/settings");
    if (!res.ok) return;
    const blob = ((await res.json()) as { settings?: unknown }).settings;
    const tabletop =
      blob && typeof blob === "object"
        ? (blob as Record<string, unknown>).tabletop
        : null;
    if (tabletop && typeof tabletop === "object") {
      (window as any).tt?.settings?.applyRemote(tabletop);
    }
  } catch {
    /* ignore */
  }
}

// --------------------------------------------------------------- styles

const CSS = `
.ttacct-chip { white-space: nowrap; }
.ttacct-panel {
  position: fixed;
  z-index: 60;
  width: 260px;
  background: rgba(24, 25, 30, 0.97);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  padding: 14px;
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  color: #e8e6df;
  font-size: 13px;
}
.ttacct-panel h3 {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #9b988e;
  margin-bottom: 10px;
}
.ttacct-panel input {
  width: 100%;
  padding: 7px 10px;
  margin-bottom: 8px;
  font-size: 13px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(0, 0, 0, 0.35);
  color: #e8e6df;
  outline: none;
  font-family: inherit;
}
.ttacct-panel input:focus { border-color: #d8b45a; }
.ttacct-status {
  min-height: 16px;
  font-size: 12px;
  color: #9b988e;
  margin-bottom: 8px;
}
.ttacct-status.error { color: #e5735d; }
.ttacct-row { display: flex; gap: 8px; }
.ttacct-row .chip { flex: 1; text-align: center; }
.ttacct-row .ttacct-primary {
  background: #d8b45a;
  border-color: #d8b45a;
  color: #221d10;
  font-weight: 600;
}
.ttacct-google {
  display: block;
  width: 100%;
  text-align: center;
  margin-top: 10px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.08);
  color: #e8e6df;
  text-decoration: none;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
}
.ttacct-google:hover { background: rgba(255, 255, 255, 0.16); }
.ttacct-hint a { color: #d8b45a; text-decoration: none; }
.ttacct-hint a:hover { text-decoration: underline; }
.ttacct-hint {
  margin-top: 10px;
  font-size: 11.5px;
  line-height: 1.4;
  color: #9b988e;
}
.ttacct-who { margin-bottom: 4px; font-weight: 600; }
.ttacct-email {
  font-size: 12px;
  color: #9b988e;
  margin-bottom: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
}
`;

// ------------------------------------------------------------------- ui

class AccountWidget {
  private chip: HTMLButtonElement;
  private panel: HTMLDivElement | null = null;

  constructor(private slot: HTMLElement) {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);

    this.chip = document.createElement("button");
    this.chip.className = "chip ttacct-chip";
    this.chip.textContent = "Sign in";
    this.chip.onclick = () => (this.panel ? this.close() : this.open());
    this.slot.appendChild(this.chip);

    document.addEventListener("pointerdown", (e) => {
      if (!this.panel) return;
      const t = e.target as Node;
      if (!this.panel.contains(t) && !this.chip.contains(t)) this.close();
    });
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.panel) this.close();
    });
  }

  setUser(user: AccountUser | null): void {
    this.chip.textContent = user ? user.name || "Account" : "Sign in";
    this.chip.title = user?.email ?? "";
    if (this.panel) {
      this.close();
      this.open();
    }
  }

  private close(): void {
    this.panel?.remove();
    this.panel = null;
  }

  private open(): void {
    this.panel = document.createElement("div");
    this.panel.className = "ttacct-panel";
    if (currentUser) this.buildSignedIn(this.panel);
    else this.buildSignedOut(this.panel);
    document.body.appendChild(this.panel);
    const r = this.chip.getBoundingClientRect();
    this.panel.style.top = `${r.bottom + 8}px`;
    this.panel.style.right = `${Math.max(8, window.innerWidth - r.right)}px`;
  }

  private buildSignedIn(p: HTMLDivElement): void {
    const u = currentUser!;
    p.innerHTML = `
      <h3>Account</h3>
      <div class="ttacct-who"></div>
      <div class="ttacct-email"></div>
      <div class="ttacct-row">
        <button class="chip ttacct-out" type="button">Sign out</button>
      </div>`;
    p.querySelector<HTMLElement>(".ttacct-who")!.textContent = u.name || "Player";
    p.querySelector<HTMLElement>(".ttacct-email")!.textContent = u.email ?? "";
    p.querySelector<HTMLButtonElement>(".ttacct-out")!.onclick = async () => {
      try {
        await postJson("/auth/logout", {});
      } catch {
        /* clear locally regardless */
      }
      setUser(null);
    };
  }

  private buildSignedOut(p: HTMLDivElement): void {
    p.innerHTML = `
      <h3>Sign in</h3>
      <input class="ttacct-mail" type="email" placeholder="email" autocomplete="email" spellcheck="false">
      <input class="ttacct-pass" type="password" placeholder="password (8+ to sign up)" autocomplete="current-password">
      <div class="ttacct-status"></div>
      <div class="ttacct-row">
        <button class="chip ttacct-primary ttacct-login" type="button">Sign in</button>
        <button class="chip ttacct-signup" type="button">Sign up</button>
      </div>`;
    const mail = p.querySelector<HTMLInputElement>(".ttacct-mail")!;
    const pass = p.querySelector<HTMLInputElement>(".ttacct-pass")!;
    const status = p.querySelector<HTMLDivElement>(".ttacct-status")!;

    const attempt = async (path: "/auth/login" | "/auth/signup") => {
      const email = mail.value.trim();
      const password = pass.value;
      if (!email || !password) {
        status.className = "ttacct-status error";
        status.textContent = "Email and password required.";
        return;
      }
      status.className = "ttacct-status";
      status.textContent = "…";
      try {
        const res = await postJson(path, { email, password });
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        if (!res.ok) {
          status.className = "ttacct-status error";
          status.textContent = body.error ?? `Failed (${res.status}).`;
          return;
        }
        await refreshMe();
        void pullSettings();
      } catch {
        status.className = "ttacct-status error";
        status.textContent = "Account service unreachable.";
      }
    };
    p.querySelector<HTMLButtonElement>(".ttacct-login")!.onclick = () => void attempt("/auth/login");
    p.querySelector<HTMLButtonElement>(".ttacct-signup")!.onclick = () => void attempt("/auth/signup");
    pass.addEventListener("keydown", (e) => {
      if (e.key === "Enter") void attempt("/auth/login");
    });

    if (sameOrigin) {
      const g = document.createElement("a");
      g.className = "ttacct-google";
      g.textContent = "Sign in with Google";
      g.href = `/sim-api/auth/google?next=${encodeURIComponent(location.pathname + location.search)}`;
      p.appendChild(g);
    } else {
      // Proxy mode: Google via device pairing (approval happens in a new tab
      // on wesyarber.net, where the Google session cookie lives).
      const g = document.createElement("button");
      g.type = "button";
      g.className = "ttacct-google";
      g.textContent = "Sign in with Google";
      const pairStatus = document.createElement("div");
      pairStatus.className = "ttacct-hint";
      g.onclick = () => void startPairing(pairStatus);
      p.append(g, pairStatus);
    }
    setTimeout(() => mail.focus(), 0);
  }
}

let widget: AccountWidget | null = null;

function setUser(user: AccountUser | null): void {
  const changed = (user?.id ?? null) !== (currentUser?.id ?? null);
  currentUser = user;
  widget?.setUser(user);
  if (changed) {
    window.dispatchEvent(new CustomEvent("tt-account-changed", { detail: user }));
  }
}

async function refreshMe(): Promise<void> {
  try {
    const res = await api("/auth/me");
    if (!res.ok) return setUser(null);
    const body = (await res.json()) as { user: AccountUser | null };
    setUser(body.user ?? null);
  } catch {
    setUser(null);
  }
}

// --------------------------------------------------------------- startup

function init(): void {
  // Expose the shared surface immediately so import-ui can use it even
  // before the slot mounts. window.tt itself is created by main.ts, but
  // import order means we may run first — create the bag if needed.
  const tt = ((window as any).tt ??= {});
  tt.account = {
    me: (): AccountUser | null => currentUser,
    api,
  };

  window.addEventListener("tt-settings-changed", ((e: CustomEvent<SettingsData>) => {
    if (currentUser) schedulePush(e.detail);
  }) as EventListener);

  // Mount into the top bar once ui.ts has built it.
  const started = Date.now();
  const tryMount = () => {
    const slot = document.querySelector<HTMLElement>(".account-slot");
    if (slot) {
      widget = new AccountWidget(slot);
      void refreshMe().then(() => {
        if (currentUser) void pullSettings();
      });
      return;
    }
    if (Date.now() - started < 15_000) setTimeout(tryMount, 100);
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tryMount);
  } else {
    tryMount();
  }
}

// Guarded so the pure helpers above are importable from bun test (no DOM).
if (typeof document !== "undefined") init();
